import { useState } from "react";
import { Send, Loader2, Sparkles, BadgeCheck, Heart, Users } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { submitMentor } from "../lib/api";
import { toast } from "sonner";

const EXPERTISE_AREAS = [
  "Subjective Coaching (5-12)",
  "Computer Education",
  "AI / Data Science",
  "Digital Marketing",
  "Graphic Design",
  "Accounts / Tally",
  "Personality Development",
  "Spoken English",
  "Career Counselling",
  "Higher Education Admission",
  "Other",
];

export default function BecomeMentor() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    expertise: "",
    experience_years: "",
    linkedin: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.expertise || !form.experience_years) {
      toast.error("Please fill name, email, phone, expertise and years of experience.");
      return;
    }
    setLoading(true);
    try {
      await submitMentor(form);
      toast.success("Thank you! Our team will reach out within 48 hours.");
      setForm({ name: "", email: "", phone: "", expertise: "", experience_years: "", linkedin: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="mentor-page">
      <PageHero
        eyebrow="Become a TASSEI Mentor"
        title="Share your expertise. Shape India's future."
        desc="Join a growing community of industry mentors helping students from rural & urban India reach their dream careers."
        image="https://images.pexels.com/photos/3184398/pexels-photo-3184398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="Mentor presenting to professionals"
      />

      <section className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          {/* benefits */}
          <div className="lg:col-span-5 space-y-4">
            {[
              { icon: Heart, t: "Give back", d: "Mentor students who are the first in their family to chase a career." },
              { icon: BadgeCheck, t: "Industry recognition", d: "Get featured on TASSEI's platforms & official communications." },
              { icon: Users, t: "Flexible engagement", d: "Workshops, 1-on-1 sessions or interview panels — you decide." },
              { icon: Sparkles, t: "Be part of a movement", d: "Help build India's No. 1 Career Success Ecosystem." },
            ].map(({ icon: Icon, t, d }, idx) => (
              <div
                key={idx}
                data-testid={`mentor-benefit-${idx}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-lg transition-all flex gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00B37E]/12 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-[#00B37E]" />
                </div>
                <div>
                  <div className="font-heading text-base font-bold text-[#0A192F]">{t}</div>
                  <div className="text-sm text-[#64748B] mt-1">{d}</div>
                </div>
              </div>
            ))}
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="mentor-form"
              className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9"
            >
              <h3 className="font-heading text-2xl font-bold text-[#0A192F]">Apply to Mentor</h3>
              <p className="text-sm text-[#64748B] mt-1">Tell us about your expertise — we&apos;ll reach out personally.</p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mname">Full Name *</Label>
                  <Input id="mname" data-testid="mentor-name" value={form.name} onChange={set("name")} placeholder="Your name" required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="mphone">Phone *</Label>
                  <Input id="mphone" data-testid="mentor-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." required className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="memail">Email *</Label>
                  <Input id="memail" data-testid="mentor-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="mexp">Area of Expertise *</Label>
                  <Select value={form.expertise} onValueChange={(v) => setForm((f) => ({ ...f, expertise: v }))}>
                    <SelectTrigger id="mexp" data-testid="mentor-expertise" className="mt-1.5 h-10">
                      <SelectValue placeholder="Select your expertise" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERTISE_AREAS.map((a) => (
                        <SelectItem key={a} value={a} data-testid={`expertise-opt-${a}`}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="myears">Years of Experience *</Label>
                  <Input id="myears" data-testid="mentor-years" value={form.experience_years} onChange={set("experience_years")} placeholder="e.g. 5 years" required className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="mlinkedin">LinkedIn / Portfolio (optional)</Label>
                  <Input id="mlinkedin" data-testid="mentor-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/..." className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="mmsg">A few words about you</Label>
                  <Textarea id="mmsg" data-testid="mentor-message" value={form.message} onChange={set("message")} placeholder="Why do you want to mentor with TASSEI?" rows={4} className="mt-1.5" />
                </div>
              </div>

              <Button type="submit" disabled={loading} data-testid="mentor-submit" className="mt-7 bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-12 px-7">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {loading ? "Submitting..." : "Submit Application"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
